import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import AdminApp from "./admin/AdminApp";
import PublicReaderApp from "./public-reader/PublicReaderApp";
import MapWindow from "./components/MapWindow";
// Phase B i18n — initialise react-i18next early so any descendant
// that calls useTranslation() resolves against the configured instance.
// The English bundles ship statically in this import; non-English bundles
// are lazy-loaded by loadLanguage() in src/i18n/index.ts.
import "./i18n";

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root element not found");

// Hostname-based routing between the three surfaces this Vite bundle
// powers:
//
//   www.emberhollow.ai      → <App />            (login + game)
//   admin.emberhollow.ai    → <AdminApp />       (operator portal)
//   story.emberhollow.ai    → <PublicReaderApp /> (public book reader)
//
// On top of those three, the main app exposes a fourth "secondary view":
// when the URL carries `?map=<storyId>` we mount <MapWindow /> — a stripped
// down second-monitor-friendly map renderer. The pop-out runs on the SAME
// origin as the main app so it shares localStorage (auth tokens) and can
// communicate with the main window via a BroadcastChannel. The map branch
// is checked BEFORE the subdomain branches because the user might pop a
// map open from the main game and end up on www.emberhollow.ai/?map=… —
// they should land in MapWindow, not the full game UI.
//
// Dev escape hatches via query param: `?admin=1` or `?subdomain=story`.
// They cover the localhost-without-DNS case without needing /etc/hosts
// edits.

const hostname =
  typeof window !== "undefined" ? window.location.hostname : "";
const params =
  typeof window !== "undefined"
    ? new URLSearchParams(window.location.search)
    : new URLSearchParams();

// Map pop-out wins ahead of all other branches. Empty / whitespace-only
// values are treated as absent so we don't render a useless MapWindow.
const mapStoryId = (() => {
  const raw = params.get("map");
  if (!raw) return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
})();

const isAdminSurface =
  hostname === "admin.emberhollow.ai" || params.get("admin") === "1";
const isStorySurface =
  !isAdminSurface &&
  (hostname.startsWith("story.") || params.get("subdomain") === "story");

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    {mapStoryId ? (
      <MapWindow storyId={mapStoryId} />
    ) : isAdminSurface ? (
      <AdminApp />
    ) : isStorySurface ? (
      <PublicReaderApp />
    ) : (
      <App />
    )}
  </React.StrictMode>,
);
